import React from 'react';
import classNames from 'classnames';

import { propTypes } from '../../../util/types';
import { ListingCard, PaginationLinks } from '../../../components';
import { parse } from '../../../util/urlHelpers';

import RequestMatchingOffersCard from './RequestMatchingOffersCard/RequestMatchingOffersCard';
import css from './SearchResultsPanel.module.css';

// Insert a "Request matching offers" promo card after every this many listings.
const OFFERS_CARD_INTERVAL = 8;

/**
 * SearchResultsPanel component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {Array<propTypes.listing>} props.listings - The listings
 * @param {propTypes.pagination} props.pagination - The pagination
 * @param {Object} props.search - The search
 * @param {Function} props.setActiveListing - The function to handle the active listing
 * @param {boolean} [props.isMapVariant] - Whether the map variant is enabled
 * @returns {JSX.Element}
 */
const SearchResultsPanel = props => {
  const {
    className,
    rootClassName,
    listings = [],
    pagination,
    search,
    setActiveListing,
    isMapVariant = true,
    listingTypeParam,
    reviews,
    intl,
    currentUser,
    onUpdateProfile,
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const pageName = listingTypeParam ? 'SearchPageWithListingType' : 'SearchPage';

  let arrivalDate = null;

  if (search && search.dates) {
    const parts = search.dates
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
    if (parts.length > 0) {
      arrivalDate = parts[0];
    }
  }

  const paginationLinks =
    pagination && pagination.totalPages > 1 ? (
      <PaginationLinks
        className={css.pagination}
        pageName={pageName}
        pagePathParams={{ listingType: listingTypeParam }}
        pageSearchParams={search}
        pagination={pagination}
        aria-label={intl.formatMessage({ id: 'SearchResultsPanel.screenreader.pagination' })}
      />
    ) : null;

  const cardRenderSizes = isMapVariant => {
    if (isMapVariant) {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 767px) 100vw',
        `(max-width: 1023px) ${panelMediumWidth}vw`,
        `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
        `${panelLargeWidth / 3}vw`,
      ].join(', ');
    } else {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 549px) 100vw',
        '(max-width: 767px) 50vw',
        `(max-width: 1439px) 26vw`,
        `(max-width: 1920px) 18vw`,
        `14vw`,
      ].join(', ');
    }
  };

  return (
    <div className={classes}>
      <ul className={isMapVariant ? css.listingCardsMapVariant : css.listingCards}>
        {listings.map((l, index) => {
          // After every OFFERS_CARD_INTERVAL listings, insert a promo card that
          // invites the user to request matching offers.
          const shouldRenderOffersCard = (index + 1) % OFFERS_CARD_INTERVAL === 0;
          return (
            <React.Fragment key={l.id.uuid}>
              <li className={css.resultItem}>
                <ListingCard
                  className={css.listingCard}
                  listing={l}
                  renderSizes={cardRenderSizes(isMapVariant)}
                  setActiveListing={setActiveListing}
                  arrivalDate={arrivalDate}
                  dates={search?.dates}
                  reviews={reviews}
                  currentUser={currentUser}
                  onUpdateProfile={onUpdateProfile}
                />
              </li>
              {shouldRenderOffersCard ? (
                <li className={css.resultItem}>
                  <RequestMatchingOffersCard />
                </li>
              ) : null}
            </React.Fragment>
          );
        })}
        {props.children}
      </ul>
      {paginationLinks}
    </div>
  );
};

export default SearchResultsPanel;
